import firestore from '@react-native-firebase/firestore';
import {RouteProp, useFocusEffect, useNavigation, useRoute} from '@react-navigation/native';
import {FlashList} from '@shopify/flash-list';
import moment from 'moment-timezone';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Image, ImageBackground, Platform, StyleSheet, Text, TouchableOpacity, View, Alert} from 'react-native';
import {KeyboardGestureArea, useKeyboardHandler} from 'react-native-keyboard-controller';
import Animated, {useAnimatedStyle, useSharedValue} from 'react-native-reanimated';
import 'react-native-get-random-values';
import {v4 as uuidv4} from 'uuid';
import {addDateHeaders} from '~Utils/chat';
import {ChevronIcon, LogoIcon} from '~assets/icons';
import {ChatInput, ChatItem, ModernChatHeader, ChatTypingIndicator} from '~components/Chat';
import ModernChatInput from '~components/Chat/ModernChatInput/ModernChatInput';
import OfflineChatInput from '~components/Chat/OfflineChatInput/OfflineChatInput';

import {SCREENS} from '~constants';
import {ThemedStatusBar} from '~components/ThemedStatusBar/ThemedStatusBar';
import useTabBar from '~containers/Core/navigation/AppScreens/zustand';
import {useGetUserAccount} from '~hooks/user/useGetUser';
import {useBlockUser, useUnblockUser} from '~hooks/user/useBlockUser';
import {useGetBlockStatus} from '~hooks/user/useGetBlockStatus';
import FirebaseChatsService from '~services/FirebaseChats';
import {CHAT_MESSAGE_TYPE_ENUM, CHAT_TYPE_ENUM, ChatType, ChatTypeWithKey, MESSAGE_STATUS} from '~types/chat';
import {MessageStatus} from '~constants/messageStatus';
import {haptics} from '~Utils/haptics';
import OfflineMessageNotification from '~components/Chat/OfflineMessageNotification/OfflineMessageNotification';
import OfflineChatStorage from '~services/OfflineChatStorage/OfflineChatStorage';
import MessageRetryService from '~services/MessageRetryService';
import NetInfo from '@react-native-community/netinfo';
import FastImage from 'react-native-fast-image';
import {spacing, typography, borderRadius, shadows} from '~constants';
import {NavigationProps, RootStackParamsList} from '~types/navigation/navigation.type';
import {useMessageStatus} from '~hooks/chat/useMessageStatus';
import {useChatStore} from '~providers/chats/zustand'; // Імпортуємо ваш Zustand store
import {logScreenView} from '~Utils/firebaseAnalytics';
import {useGetBusinessAccount} from '~hooks/business/useGetBusinessAccount';
import {Business} from '~types/api/business';
import {User} from '~types/api/user';
import {Notifier, NotifierComponents} from 'react-native-notifier';
import LikeButton from '~components/LikeButton';
import {useTheme} from '~contexts/ThemeContext';
import {useUserStore} from '~providers/userStore/zustand';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';

const useKeyboardAnimationAndroid = () => {
  // Removed useTheme() as it was causing unnecessary re-renders
  // const {colors} = useTheme();
  const progress = useSharedValue(0);
  const height = useSharedValue(0);

  useKeyboardHandler({
    onMove: e => {
      'worklet';

      if (e && typeof e.progress === 'number' && typeof e.height === 'number') {
        progress.value = e.progress;
        height.value = e.height;
      }
    },
    onInteractive: e => {
      'worklet';

      if (e && typeof e.progress === 'number' && typeof e.height === 'number') {
        progress.value = e.progress;
        height.value = e.height;
      }
    },
  });

  return {height, progress};
};

const useKeyboardAnimationIos = () => {
  const progress = useSharedValue(0);
  const height = useSharedValue(0);
  const shouldUseOnMoveHandler = useSharedValue(false);
  useKeyboardHandler({
    onStart: e => {
      'worklet';

      if (e && typeof e.height === 'number' && typeof e.progress === 'number') {
        if (progress.value !== 1 && progress.value !== 0 && e.height !== 0) {
          shouldUseOnMoveHandler.value = true;
          return;
        }

        progress.value = e.progress;
        height.value = e.height;
      }
    },
    onInteractive: e => {
      'worklet';

      if (e && typeof e.progress === 'number' && typeof e.height === 'number') {
        progress.value = e.progress;
        height.value = e.height;
      }
    },
    onMove: e => {
      'worklet';

      if (e && typeof e.progress === 'number' && typeof e.height === 'number' && shouldUseOnMoveHandler.value) {
        progress.value = e.progress;
        height.value = e.height;
      }
    },
    onEnd: e => {
      'worklet';

      if (e && typeof e.height === 'number' && typeof e.progress === 'number') {
        height.value = e.height;
        progress.value = e.progress;
        shouldUseOnMoveHandler.value = false;
      }
    },
  });

  return {height, progress};
};

const AnimatedFlatList = Animated.createAnimatedComponent(FlashList);

const useKeyboardAnimation = Platform.OS === 'android' ? useKeyboardAnimationAndroid : useKeyboardAnimationIos;

const ChatWithUsers = () => {
  console.log('🚨🚨🚨 ChatWithUsers: COMPONENT IS RENDERING! 🚨🚨🚨');
  const {colors} = useTheme();
  const {setIsTabBarDisabled} = useTabBar();
  const {params} = useRoute<RouteProp<RootStackParamsList, SCREENS.USER_CHAT>>();

  // Add authentication check
  const user = useUserStore(state => state.user);
  const currentUserId = user?.uid;
  const {data: currentUser} = useGetUserAccount(currentUserId || '');
  const {image, userName, fromEventId, fromScreen} = params;

  // Early return if no authentication
  if (!currentUserId) {
    console.log('ChatWithUsers: No authenticated user found');
    return (
      <View style={{flex: 1, backgroundColor: colors.background, justifyContent: 'center', alignItems: 'center'}}>
        <Text style={{color: colors.textPrimary}}>Authentication required...</Text>
      </View>
    );
  }
  const {data: host} = useGetBusinessAccount('CmCCvWvpRVhhFlHdgU9QE6hbun82');
  const navigation = useNavigation<NavigationProps>();
  const {t} = useTranslation();
  // Extract functions separately to prevent infinite loops
  const acceptChat = useChatStore(state => state.acceptChat);
  const checkChatAcceptance = useChatStore(state => state.checkChatAcceptance);
  const isAcceptedChats = useChatStore(state => state.isAcceptedChats);

  const defaultCallback = () => {
    // If we have navigation context from an event, navigate back to that event
    if (fromEventId && fromScreen === 'EventDetails') {
      console.log('Navigating back to event details:', fromEventId);
      navigation.goBack();
    } else {
      // Default back navigation
      navigation.replace(SCREENS.CHATS);
      navigation.navigate(SCREENS.CHAT_STACK, {
        key: null,
        image: '',
        userName: '',
      });
    }
  };

  // Hide tab bar when chat is focused
  useFocusEffect(
    useCallback(() => {
      setIsTabBarDisabled(true);
      return () => {
        setIsTabBarDisabled(false);
      };
    }, [setIsTabBarDisabled]),
  );

  useEffect(() => {
    logScreenView('Chat With Users', 'ChatWithUsers');
  }, []);

  const key = params?.chatId;

  const [currentChat, setCurrentChat] = useState<ChatTypeWithKey | null>(null);
  const [isAccepted, setIsAccepted] = useState<boolean | null>(null);
  const [isInitiator, setIsInitiator] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isUserBlocked, setIsUserBlocked] = useState(false);

  // Offline message notification state
  const [showOfflineNotification, setShowOfflineNotification] = useState(false);
  const [offlineNotificationData, setOfflineNotificationData] = useState({
    successCount: 0,
    failureCount: 0,
  });

  // Offline messages state
  const [offlineMessages, setOfflineMessages] = useState<any[]>([]);

  // Network state
  const [isOnline, setIsOnline] = useState(true);

  // Message status tracking
  const {updateMessageStatus, getMessageStatus, pendingMessages, addPendingMessage} = useMessageStatus();

  // Use useState to prevent infinite loops with user ID calculation
  const [otherUserId, setOtherUserId] = useState<string>('');

  // Combine Firestore messages with offline messages
  const combinedMessages = useMemo(() => {
    const firestoreMessages = currentChat?.history || [];

    // Combine and sort all messages by timestamp
    const allMessages = [...firestoreMessages, ...offlineMessages];
    return allMessages.sort((a: any, b: any) => {
      const timeA = new Date(a.timestamp).getTime();
      const timeB = new Date(b.timestamp).getTime();
      return timeA - timeB; // Oldest first
    });
  }, [currentChat?.history, offlineMessages]);

  // Update otherUserId only when currentChat.userIds actually changes
  useEffect(() => {
    if (currentChat?.userIds) {
      const foundUserId = currentChat.userIds.find(user => user.toLowerCase() !== (currentUserId || '').toLowerCase());
      if (foundUserId && foundUserId !== otherUserId) {
        setOtherUserId(foundUserId);
      }
    }
  }, [currentChat?.userIds, otherUserId, currentUserId]);

  const {data: otherUser} = useGetUserAccount(otherUserId, false);
  const {data: otherBusinessUser} = useGetBusinessAccount(otherUserId, false);

  // API-based block functionality
  const {mutateAsync: blockUserAPI, isLoading: isBlockingUserAPI} = useBlockUser();
  const {mutateAsync: unblockUserAPI, isLoading: isUnblockingUserAPI} = useUnblockUser();
  const {data: apiBlockStatus, refetch: refetchBlockStatus} = useGetBlockStatus(otherUserId);

  // Remove this problematic useEffect that's causing infinite re-renders
  // The data will be fetched automatically by the hooks when userIds change
  // useEffect(() => {
  //   if (currentChat?.userIds && currentChat.userIds[userIndex || 0]) {
  //     refetchBusinessAccount().then(data => {
  //       if (!data.data) {
  //         refetchUserAccount();
  //       }
  //     });
  //   }
  // }, [currentChat?.userIds, userIndex, refetchBusinessAccount, refetchUserAccount]);

  const isConfirmationRequired = useMemo(() => {
    // Temporarily disable to fix infinite loop
    return false;
    // if (currentChat && currentChat.history && currentChat.history.length > 1) {
    //   const isConfirmMessage = currentChat.history[0];
    //   if (isConfirmMessage.type == CHAT_MESSAGE_TYPE_ENUM.ISSUE_CONFIRMATION) {
    //     return true;
    //   }
    //   return false;
    // }
    // return false;
  }, []);

  // Re-enable keyboard animation for proper chat input positioning
  const {height, progress} = useKeyboardAnimation();

  const flatListRef = useRef<any>(null);

  // Fix keyboard handling - use paddingBottom instead of marginBottom
  const scrollViewStyle = useAnimatedStyle(
    () => ({
      flex: 1,
      paddingBottom: height.value,
    }),
    [],
  );

  // Simplified chat loading to prevent infinite loops
  useEffect(() => {
    if (!key || !currentUserId) {
      console.log('ChatWithUsers: Missing key or currentUserId', {key, currentUserId});
      return;
    }

    let isMounted = true;
    console.log('ChatWithUsers: Setting up chat listener for key:', key);

    const loadChatData = async () => {
      try {
        // Set up real-time chat listener first
        const unsubscribe = firestore()
          .collection('chats')
          .doc(key)
          .onSnapshot(
            documentSnapshot => {
              if (!isMounted) return;

              console.log('ChatWithUsers: Chat document snapshot received', {
                exists: documentSnapshot.exists,
                id: documentSnapshot.id,
              });

              const data = documentSnapshot.data() as ChatTypeWithKey | undefined;
              if (data) {
                console.log('ChatWithUsers: Chat data loaded', {
                  type: data.type,
                  userIds: data.userIds,
                  historyLength: data.history?.length || 0,
                });

                // Sort messages by timestamp to ensure correct order
                const sortedMessages = (data.history || []).sort((a: any, b: any) => {
                  const timeA = new Date(a.timestamp).getTime();
                  const timeB = new Date(b.timestamp).getTime();
                  return timeA - timeB; // Oldest first, since list is inverted
                });

                setCurrentChat({
                  ...data,
                  history: sortedMessages,
                  key: documentSnapshot.id,
                } as ChatTypeWithKey);

                // Check if current user is blocked
                const blockedUsers = data.blockedUsers || {};
                const isCurrentUserBlocked = blockedUsers[currentUserId] === true;
                setIsUserBlocked(isCurrentUserBlocked);

                // Check if current user is the chat initiator
                const isCurrentUserInitiator = data.userIds?.[0] === currentUserId;
                setIsInitiator(isCurrentUserInitiator);
                console.log('ChatWithUsers: User is initiator:', isCurrentUserInitiator);

                // Set acceptance logic based on chat type and user role
                if (data.type === 'contact-pyxi-select' || data.type === CHAT_TYPE_ENUM.ORGANISATION) {
                  // Support chats and organisation chats are always accepted
                  setIsAccepted(true);
                } else if (isCurrentUserInitiator) {
                  // Initiators of user-to-user chats are always accepted
                  setIsAccepted(true);
                } else {
                  // For non-initiators of user-to-user chats, check AsyncStorage
                  checkChatAcceptance(key)
                    .then(accepted => {
                      if (isMounted) {
                        setIsAccepted(accepted);
                      }
                    })
                    .catch(error => {
                      console.error('ChatWithUsers: Error checking chat acceptance:', error);
                      if (isMounted) {
                        // Default to false for user-to-user chats if there's an error
                        setIsAccepted(false);
                      }
                    });
                }
              } else {
                console.log('ChatWithUsers: No chat data found for document');
              }
            },
            error => {
              console.error('ChatWithUsers: Chat subscription error:', error);
              if (isMounted) {
                setIsAccepted(false);
              }
            },
          );

        return unsubscribe;
      } catch (error) {
        console.error('Error loading chat data:', error);
        if (isMounted) {
          setIsAccepted(false);
        }
      }
    };

    const unsubscribePromise = loadChatData();

    return () => {
      isMounted = false;
      unsubscribePromise.then(unsubscribe => {
        if (unsubscribe) {
          unsubscribe();
        }
      });
    };
  }, [key, currentUserId, checkChatAcceptance]); // Include necessary dependencies

  // Network state monitoring
  useEffect(() => {
    const unsubscribe = NetInfo.addEventListener(state => {
      const isNowOnline = Boolean(state.isConnected && state.isInternetReachable);
      setIsOnline(isNowOnline);
    });

    return () => unsubscribe();
  }, []);

  // Initialize retry service
  useEffect(() => {
    const retryService = MessageRetryService.getInstance();
    retryService.initialize();

    return () => {
      retryService.cleanup();
    };
  }, []);

  // Extracted loadOfflineMessages function for reuse
  const loadOfflineMessages = async () => {
    if (!key) return;

    try {
      const unsentMessages = await OfflineChatStorage.getUnsentMessages();
      const chatOfflineMessages = unsentMessages
        .filter(msg => msg.chatId === key)
        .map(msg => ({
          id: msg.tempMessageId,
          message: msg.text,
          text: msg.text,
          timestamp: msg.timestamp,
          user_id: msg.userId,
          sender_id: msg.userId,
          senderId: msg.userId,
          user_name: msg.userName,
          sender: msg.userName,
          senderName: msg.userName,
          user_image: msg.userImage || '',
          sender_image: msg.userImage || '',
          status: msg.status,
          messageStatus: msg.status,
          isPending: true,
        }));

      setOfflineMessages(chatOfflineMessages);
      console.log('📱 Loaded offline messages for chat:', {chatId: key, count: chatOfflineMessages.length});
    } catch (error) {
      console.error('Failed to load offline messages:', error);
    }
  };

  // Load offline messages and try to sync when chat opens
  useEffect(() => {
    if (!key) return;

    // Load offline messages immediately for display
    loadOfflineMessages();

    // Try to send unsent messages when chat opens (if online)
    if (isOnline) {
      const retryService = MessageRetryService.getInstance();
      retryService.retryChatMessages(key, onSendMessage, {
        onMessageStatusChange: handleMessageStatusChange,
        onOfflineMessagesSent: handleOfflineMessagesSent,
      });
    }
  }, [key, isOnline]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (combinedMessages && combinedMessages.length > 0) {
      setTimeout(() => {
        flatListRef?.current?.scrollToEnd({animated: true});
      }, 100);
    }
  }, [combinedMessages.length]);

  // Removed unreadMessagesCount useMemo as it was causing infinite loops and not being used

  // Handle message status changes
  const handleMessageStatusChange = (tempId: string, status: MessageStatus, realMessageId?: string) => {
    updateMessageStatus(tempId, status, realMessageId);

    // Refresh offline messages when status changes
    if (status === 'pending') {
      // Reload offline messages to show the new one
      const loadOfflineMessages = async () => {
        try {
          const unsentMessages = await OfflineChatStorage.getUnsentMessages();
          const chatOfflineMessages = unsentMessages
            .filter(msg => msg.chatId === key)
            .map(msg => ({
              id: msg.tempMessageId,
              message: msg.text, // Use 'message' instead of 'text'
              text: msg.text, // Keep both for compatibility
              timestamp: msg.timestamp,
              user_id: msg.userId,
              sender_id: msg.userId, // Add sender_id for ChatItem compatibility
              senderId: msg.userId, // Add senderId for ChatItem compatibility
              user_name: msg.userName,
              sender: msg.userName, // Add sender for ChatItem compatibility
              senderName: msg.userName, // Add senderName for ChatItem compatibility
              user_image: msg.userImage || '',
              sender_image: msg.userImage || '', // Add sender_image for ChatItem compatibility
              status: msg.status,
              messageStatus: msg.status, // Add messageStatus for ModernChatBubble
              isPending: true,
            }));

          setOfflineMessages(chatOfflineMessages);
        } catch (error) {
          console.error('Failed to refresh offline messages:', error);
        }
      };

      loadOfflineMessages();
    }
  };

  // Handle offline messages sent notification
  const handleOfflineMessagesSent = (successCount: number, failureCount: number) => {
    console.log(`📤 Offline messages result: ${successCount} success, ${failureCount} failed`);

    // Update notification data and show notification
    setOfflineNotificationData({successCount, failureCount});
    setShowOfflineNotification(true);

    // Provide haptic feedback
    if (successCount > 0) {
      haptics.success();
    } else if (failureCount > 0) {
      haptics.error();
    }
  };

  // Enhanced Block/Unblock functionality - combines Firebase and API
  const handleBlockUser = async () => {
    try {
      if (!currentUserId || !otherUserId) {
        Alert.alert('Error', 'Unable to block user. Please try again.');
        return;
      }

      // Call API first
      await blockUserAPI({
        blocked_id: otherUserId,
        reason: 'Blocked from chat',
      });

      // Update Firebase chat document
      const chatRef = firestore().collection('chats').doc(key);
      await chatRef.update({
        [`blockedUsers.${currentUserId}`]: true,
        [`blockedBy.${otherUserId}`]: currentUserId,
      });

      // Update local states
      setIsUserBlocked(true);

      // Refresh API block status
      refetchBlockStatus();

      haptics.success();

      Alert.alert(
        'User Blocked',
        `${userName || 'User'} has been blocked. You will no longer be able to send or receive messages from this user.`,
        [{text: 'OK'}],
      );
    } catch (error) {
      console.error('Error blocking user:', error);
      Alert.alert('Error', 'Failed to block user. Please try again.');
    }
  };

  const handleUnblockUser = async () => {
    try {
      if (!currentUserId || !otherUserId) {
        Alert.alert('Error', 'Unable to unblock user. Please try again.');
        return;
      }

      // Call API first
      await unblockUserAPI({
        blocked_id: otherUserId,
        reason: 'Unblocked from chat',
      });

      // Update Firebase chat document
      const chatRef = firestore().collection('chats').doc(key);
      await chatRef.update({
        [`blockedUsers.${currentUserId}`]: firestore.FieldValue.delete(),
        [`blockedBy.${otherUserId}`]: firestore.FieldValue.delete(),
      });

      // Update local states
      setIsUserBlocked(false);

      // Refresh API block status
      refetchBlockStatus();

      haptics.success();

      Alert.alert(
        'User Unblocked',
        `${userName || 'User'} has been unblocked. You can now send and receive messages from this user again.`,
        [{text: 'OK'}],
      );
    } catch (error) {
      console.error('Error unblocking user:', error);
      Alert.alert('Error', 'Failed to unblock user. Please try again.');
    }
  };

  const confirmBlockUser = () => {
    Alert.alert(
      'Block User',
      `Are you sure you want to block ${userName || 'this user'}? You will not be able to send or receive messages from them.`,
      [
        {text: 'Cancel', style: 'cancel'},
        {text: 'Block', style: 'destructive', onPress: handleBlockUser},
      ],
    );
  };

  const confirmUnblockUser = () => {
    Alert.alert('Unblock User', `Are you sure you want to unblock ${userName || 'this user'}?`, [
      {text: 'Cancel', style: 'cancel'},
      {text: 'Unblock', style: 'default', onPress: handleUnblockUser},
    ]);
  };

  // Determine final block status - prioritize API status, fallback to Firebase
  const finalBlockStatus = useMemo(() => {
    // Use API status if available
    if (apiBlockStatus?.is_blocked !== undefined) {
      return apiBlockStatus.is_blocked;
    }
    // Fall back to Firebase status
    return isUserBlocked;
  }, [apiBlockStatus?.is_blocked, isUserBlocked]);

  // Check if this is a GROUP chat
  const isGroupChat = useMemo(() => {
    return currentChat?.type === CHAT_TYPE_ENUM.GROUP;
  }, [currentChat?.type]);

  // Handle leaving a group chat
  const handleLeaveGroup = async () => {
    try {
      if (!currentChat || !currentUserId) {
        Alert.alert('Error', 'Unable to leave group. Please try again.');
        return;
      }

      // Remove current user from the group
      const updatedUserIds = currentChat.userIds.filter(userId => userId !== currentUserId);
      const updatedUsers = currentChat.users.filter((_, index) => currentChat.userIds[index] !== currentUserId);

      // Add system message about user leaving
      const systemMessage = {
        message_id: uuidv4(),
        message: `${currentUser?.first_name || 'User'} has left the group`,
        type: CHAT_MESSAGE_TYPE_ENUM.INFO,
        sender_id: '',
        sender: 'System',
        sender_image: '',
        readUserIds: [],
        timestamp: moment().toISOString(),
      };

      const chatRef = firestore().collection('chats').doc(key);
      const updatedHistory = [...currentChat.history, systemMessage];

      // Remove from muted users if exists
      const updatedMutedUsers = {...(currentChat as any).mutedUsers};
      delete updatedMutedUsers[currentUserId];

      await chatRef.update({
        userIds: updatedUserIds,
        users: updatedUsers,
        history: updatedHistory,
        mutedUsers: updatedMutedUsers,
      });

      haptics.success();

      Notifier.showNotification({
        title: `You have left ${currentChat.eventName || 'the group'}`,
        Component: NotifierComponents.Alert,
        componentProps: {
          alertType: 'success',
        },
      });

      // Navigate back to chat list
      navigation.goBack();
    } catch (error) {
      console.error('Error leaving group:', error);
      Alert.alert('Error', 'Failed to leave group. Please try again.');
    }
  };

  // Confirm before leaving group
  const confirmLeaveGroup = () => {
    Alert.alert(
      'Leave Group',
      `Are you sure you want to leave ${currentChat?.eventName || 'this group'}? This action cannot be undone.`,
      [
        {text: 'Cancel', style: 'cancel'},
        {text: 'Leave', style: 'destructive', onPress: handleLeaveGroup},
      ],
    );
  };

  // Enhanced block user for group chats - includes removing user from group
  const handleBlockUserInGroup = async () => {
    try {
      if (!currentChat || !currentUserId || !otherUserId) {
        Alert.alert('Error', 'Unable to block user. Please try again.');
        return;
      }

      // First, try to block via API
      await blockUserAPI({
        blocked_id: otherUserId,
        reason: 'Blocked from group chat',
      });

      // Update Firebase chat document
      const chatRef = firestore().collection('chats').doc(key);

      // Remove user from the group
      const updatedUserIds = currentChat.userIds.filter(userId => userId !== otherUserId);
      const updatedUsers = currentChat.users.filter((_, index) => currentChat.userIds[index] !== otherUserId);

      // Add system message
      const systemMessage = {
        message_id: uuidv4(),
        message: `${currentChat.users[currentChat.userIds.indexOf(otherUserId)] || 'User'} has been removed from the group`,
        type: CHAT_MESSAGE_TYPE_ENUM.INFO,
        sender_id: '',
        sender: 'System',
        sender_image: '',
        readUserIds: [],
        timestamp: moment().toISOString(),
      };

      const updatedHistory = [...currentChat.history, systemMessage];

      // Remove from muted users if exists
      const updatedMutedUsers = {...(currentChat as any).mutedUsers};
      delete updatedMutedUsers[otherUserId];

      await chatRef.update({
        userIds: updatedUserIds,
        users: updatedUsers,
        history: updatedHistory,
        mutedUsers: updatedMutedUsers,
        [`blockedUsers.${currentUserId}`]: true,
        [`blockedBy.${otherUserId}`]: currentUserId,
      });

      setIsUserBlocked(true);
      refetchBlockStatus();
      haptics.success();

      Alert.alert('User Blocked and Removed', `${userName || 'User'} has been blocked and removed from the group.`, [
        {text: 'OK'},
      ]);
    } catch (error) {
      console.error('Error blocking user in group:', error);
      Alert.alert('Error', 'Failed to block user. Please try again.');
    }
  };

  // Confirm before blocking user in group
  const confirmBlockUserInGroup = () => {
    Alert.alert(
      'Block User',
      `Are you sure you want to block ${userName || 'this user'}? They will be removed from the group and you won't be able to communicate with them.`,
      [
        {text: 'Cancel', style: 'cancel'},
        {text: 'Block', style: 'destructive', onPress: handleBlockUserInGroup},
      ],
    );
  };

  const onSendMessage = async (text: string, tempMessageId?: string) => {
    try {
      console.log('ChatWithUsers: onSendMessage called with:', {
        text,
        key,
        userId: currentUserId,
        userName: `${currentUser?.first_name || 'First name'}`,
        currentChatStatus: currentChat?.status,
        currentChatType: currentChat?.type,
        tempMessageId,
        isAccepted,
        isInitiator,
      });

      // Check if user is blocked before sending message
      if (finalBlockStatus) {
        Alert.alert('Cannot Send Message', 'You have blocked this user. Unblock them first to send messages.', [
          {text: 'OK'},
        ]);
        throw new Error('User is blocked');
      }

      // Check if current user is blocked by the other user
      const blockedBy = currentChat?.blockedBy || {};
      const isBlockedByOtherUser = blockedBy[currentUserId];
      if (isBlockedByOtherUser) {
        Alert.alert('Cannot Send Message', 'This user has blocked you. Your message cannot be delivered.', [
          {text: 'OK'},
        ]);
        throw new Error('User is blocked by recipient');
      }

      // Validate required parameters
      if (!key) {
        console.error('ChatWithUsers: Missing chat key');
        throw new Error('Chat ID is required');
      }
      if (!currentUserId) {
        console.error('ChatWithUsers: Missing current user ID');
        throw new Error('User authentication required');
      }
      if (!text?.trim()) {
        console.error('ChatWithUsers: Empty message text');
        throw new Error('Message text is required');
      }

      // Simplified chat status handling - only update status if needed
      if (currentChat && currentChat?.status !== 'open' && currentChat?.type !== 'contact-pyxi-select') {
        console.log('ChatWithUsers: Chat is not open, updating status to open...');
        const chatRef = firestore().collection('chats').doc(key);
        await chatRef.update({status: 'open'});
        console.log('ChatWithUsers: Chat status updated to open');
      }

      console.log('ChatWithUsers: Scroll to bottom ');
      // Scroll to bottom (end of list) since we removed inverted
      try {
        flatListRef?.current?.scrollToEnd({animated: true});
      } catch (error) {}
      console.log('ChatWithUsers: Scroll to bottom ');
      const messageId = await FirebaseChatsService.pushMessageForUsers({
        chat_id: key,
        user_id: currentUserId,
        user_image: currentUser?.photo || '',
        user_name: `${currentUser?.first_name || 'First name'}`,
        text: text,
        temp_message_id: tempMessageId,
      });
      console.log('ChatWithUsers: Message sent successfully');
      return messageId;
    } catch (error) {
      console.error('ChatWithUsers: Error sending message:', error);
      throw error; // Re-throw so ChatInput can handle it
    }
  };

  const handleAccept = async () => {
    setIsAccepted(true);
    await acceptChat(key); // Зберегти стан прийняття чату
  };

  const handleReject = async () => {
    await firestore().collection('chats').doc(key).delete();
    navigation.goBack();
  };

  const onEventIssueClick = () => {
    onSendMessage(
      `Thanks for confirming it's an event related issue, ${currentChat?.users[0]}! Can you please provide more details about the issue?`,
    );
  };

  const onCloseIssue = async () => {
    const chatRef = firestore().collection('chats').doc(key);
    await chatRef.update({status: 'closed'});
    navigation.goBack();
    Notifier.showNotification({
      title: 'Your support issue has been successfully closed. If you need further assistance, feel free to reach out!',
      Component: NotifierComponents.Alert,
      componentProps: {
        alertType: 'success',
      },
    });
  };

  const onTechnicalIssueClick = async () => {
    const chatId = await FirebaseChatsService.createOrganisationChat({
      user_id1: currentUserId,
      user_id2: host?.uid + '',
      user_name1: `${currentUser?.first_name} ${currentUser?.last_name || ''}`,
      user_name2: (host as Business)?.name
        ? (host as Business)?.name
        : (host as unknown as User)?.last_name
          ? `${(host as unknown as User)?.first_name} ${(host as unknown as User)?.last_name || ''}`
          : (host as unknown as User)?.first_name || '',
      user_image: currentUser?.photo + '',
      user_image2: (host as Business)?.photo || (host as unknown as User)?.photo || '',
      isTechnical: true,
      event: null,
    });

    const chatRef = firestore().collection('chats').doc(chatId);
    const doc = await chatRef.get();
    if (doc.exists) {
      const chatData = doc.data as unknown as ChatType;
      const updatedMessages = chatData.history.map((message: any) => {
        if (!message.readUserIds?.includes(currentUserId)) {
          console.log('Updating message:', message);
          return {...message, readUserIds: [...(message.readUserIds || []), currentUserId]};
        }
        return message;
      });

      await chatRef.update({history: updatedMessages});
    }

    // No need to navigate since we're already in the CHAT_STACK
    // The chat will be updated automatically through the chat listeners
  };

  // Move styles useMemo to before conditional returns to fix hooks ordering
  const styles = useMemo(
    () =>
      StyleSheet.create({
        // Modern Event Card Styles
        modernEventCard: {
          backgroundColor: colors.surface,
          borderRadius: borderRadius.xl,
          marginHorizontal: spacing.md,
          marginTop: spacing.sm,
          marginBottom: spacing.xs,
          overflow: 'hidden',
          borderWidth: 1,
          borderColor: colors.border + '30',
          ...shadows.sm,
        },
        eventImageContainer: {
          height: 120,
          position: 'relative',
        },
        eventImage: {
          width: '100%',
          height: '100%',
        },
        eventImageOverlay: {
          ...StyleSheet.absoluteFillObject,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
        },
        eventTopRow: {
          position: 'absolute',
          top: spacing.sm,
          right: spacing.sm,
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: spacing.xs,
        },
        eventLikeButton: {
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          borderRadius: borderRadius.full,
          padding: spacing.xs,
          ...shadows.sm,
        },
        eventTitleOverlay: {
          position: 'absolute',
          bottom: spacing.sm,
          left: spacing.sm,
          right: spacing.sm,
        },
        eventTitle: {
          fontSize: typography.fontSize.lg,
          fontWeight: typography.fontWeight.bold,
          color: colors.white,
          textShadowColor: 'rgba(0, 0, 0, 0.5)',
          textShadowOffset: {width: 0, height: 1},
          textShadowRadius: 2,
        },
        eventContentContainer: {
          padding: spacing.sm,
          gap: spacing.xs,
        },
        eventDateText: {
          fontSize: typography.fontSize.sm,
          color: colors.textSecondary,
          fontWeight: typography.fontWeight.medium,
        },
        eventLocationText: {
          fontSize: typography.fontSize.sm,
          color: colors.textSecondary,
        },
        eventPriceContainer: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: spacing.xs,
        },
        eventPriceText: {
          fontSize: typography.fontSize.base,
          fontWeight: typography.fontWeight.semibold,
          color: colors.primary,
        },
        eventChatBadge: {
          backgroundColor: colors.primary + '20',
          paddingHorizontal: spacing.sm,
          paddingVertical: spacing.xs,
          borderRadius: borderRadius.md,
        },
        eventChatBadgeText: {
          fontSize: typography.fontSize.xs,
          fontWeight: typography.fontWeight.medium,
          color: colors.primary,
        },
      }),
    [colors],
  );

  if (isConfirmationRequired) {
    return (
      <View style={{flex: 1}}>
        <TouchableOpacity
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            paddingHorizontal: 20,
            paddingBottom: 10,
            marginTop: Platform.OS === 'android' ? 30 : 50,
          }}
          onPress={defaultCallback}>
          <ChevronIcon />
          <Text style={{color: colors.primary, fontWeight: '600'}}>{t('generic.back')}</Text>
        </TouchableOpacity>
        <KeyboardGestureArea interpolator="ios" enableSwipeToDismiss style={{flex: 1}}>
          <Animated.View style={[scrollViewStyle, {paddingHorizontal: 5}]} />
        </KeyboardGestureArea>

        <View
          style={{
            padding: 20,
            borderTopWidth: 1,
            borderTopColor: colors.gray400,
            backgroundColor: colors.gray100,
          }}>
          <Text style={{fontSize: 14, marginBottom: 20, textAlign: 'center'}}>{currentChat?.history[0].message}</Text>

          <View style={{flexDirection: 'row', justifyContent: 'space-between'}}>
            <TouchableOpacity
              style={{
                padding: 12,
                borderRadius: 18,
                marginRight: 10,
                flex: 1,
                alignItems: 'center',
                backgroundColor: colors.secondary,
              }}
              onPress={onTechnicalIssueClick}>
              <Text style={{color: colors.white, fontWeight: '600'}}>{t('technicalissue')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={{
                padding: 12,
                backgroundColor: colors.secondary,
                borderRadius: 18,
                marginLeft: 10,
                flex: 1,
                alignItems: 'center',
              }}
              onPress={onEventIssueClick}>
              <Text style={{color: colors.white, fontWeight: '600'}}>{t('eventissue')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  }

  // Show loading state while checking acceptance
  if (isAccepted === null) {
    return (
      <View style={{flex: 1, backgroundColor: colors.background, justifyContent: 'center', alignItems: 'center'}}>
        <Text style={{color: colors.textPrimary}}>Loading...</Text>
      </View>
    );
  }

  // If user is initiator or chat type is  always show the chat
  if (isInitiator || currentChat?.type === 'contact-pyxi-select') {
    // Continue to render the chat normally
  } else if (!isAccepted) {
    // Show acceptance UI for non-initiators who haven't accepted yet
    // This will be handled by the conditional render later in the component
  }

  const selectedEvent = currentChat?.event;

  const onEventClick = () => {
    if (selectedEvent) {
      navigation.navigate(SCREENS.HOME_STACK, {
        eventId: selectedEvent.event_id,
      });
    }
  };

  const onHeaderUserClick = () => {
    if (otherUser?.uid) {
      navigation.navigate(SCREENS.PERSONAL_INFO, {
        user: {
          tag: otherUser?.uid + 'image',
          source: otherUser?.photo || '',
          description: otherUser?.description || '',
          name: `${otherUser?.first_name} ${otherUser?.last_name || ''}`,
          user_id: otherUser?.uid || '',
          eventName: '',
        },
      });
    } else if (otherBusinessUser?.uid) {
      navigation.navigate(SCREENS.PERSONAL_INFO, {
        user: {
          tag: otherBusinessUser?.uid + 'image',
          source: otherBusinessUser?.photo || '',
          description: otherBusinessUser?.description || '',
          name: `${otherBusinessUser?.name}`,
          user_id: otherBusinessUser?.uid || '',
          eventName: '',
        },
      });
    }
  };

  return (
    <View style={{flex: 1, backgroundColor: colors.background}}>
      <ThemedStatusBar />
      <ModernChatHeader
        userName={userName || 'User'}
        userImage={image}
        isOnline={false}
        chatWIthUser={true}
        issueNumber={currentChat?.issueNumber?.[0]?.toString()}
        onBackPress={defaultCallback}
        onUserPress={onHeaderUserClick}
        showCallButtons={false}
        showMoreButton={false}
        showBlockButton={!!(otherUserId && currentChat?.type !== 'contact-pyxi-select')}
        isUserBlocked={finalBlockStatus}
        onBlockPress={isGroupChat ? confirmBlockUserInGroup : finalBlockStatus ? confirmUnblockUser : confirmBlockUser}
        showLeaveGroupButton={isGroupChat}
        onLeaveGroupPress={confirmLeaveGroup}
      />

      <KeyboardGestureArea interpolator="ios" enableSwipeToDismiss style={{flex: 1}}>
        <Animated.View style={[scrollViewStyle, {paddingHorizontal: 5, flex: 1}]}>
          <AnimatedFlatList
            ref={flatListRef}
            data={combinedMessages}
            renderItem={({item, index}) => (
              <ChatItem item={item} chatType={currentChat?.type || 'user'} index={index} />
            )}
            keyExtractor={(item: any, index) => item.id || `key_${index}`}
            showsVerticalScrollIndicator={true}
            estimatedItemSize={100}
            contentContainerStyle={{paddingBottom: 10}}
            keyboardShouldPersistTaps="handled"
            ListFooterComponent={() => <ChatTypingIndicator isVisible={isTyping} userName={userName || 'User'} />}
          />
        </Animated.View>
      </KeyboardGestureArea>

      {currentChat?.status != 'open' && currentChat?.issueNumber && (
        <Text style={{alignSelf: 'center', marginTop: 10, marginBottom: 10, color: colors.gray400}}>
          Chat #{currentChat?.issueNumber.length > 0 ? currentChat?.issueNumber[0] : ''} is closed, to reopen it send a
          new message.
        </Text>
      )}

      {finalBlockStatus && (
        <View
          style={{
            backgroundColor: colors.error + '20',
            paddingHorizontal: 20,
            paddingVertical: 12,
            borderTopWidth: 1,
            borderBottomWidth: 1,
            borderColor: colors.error + '30',
            marginVertical: 5,
            marginBottom: 30,
          }}>
          <View style={{flexDirection: 'row', alignItems: 'center', justifyContent: 'center'}}>
            <MaterialIcons name="block" size={16} color={colors.error} />
            <Text
              style={{
                color: colors.error,
                fontSize: 14,
                fontWeight: '500',
                marginLeft: 8,
                textAlign: 'center',
              }}>
              You have blocked {userName || 'this user'}. Unblock to send messages.
            </Text>
          </View>
        </View>
      )}

      {isAccepted === false && !isInitiator && currentChat?.type !== 'contact-pyxi-select' && (
        <View
          style={{
            padding: 20, // Збільшено padding для збільшення висоти блоку
            borderTopWidth: 1,
            borderTopColor: colors.gray400,
            backgroundColor: colors.gray100,
          }}>
          {currentChat?.eventName ? (
            <Text style={{fontSize: 14, marginBottom: 20, textAlign: 'center'}}>
              {currentChat?.users[1]} wants to chat with you about {currentChat?.eventName}.
            </Text>
          ) : (
            <Text style={{fontSize: 14, marginBottom: 20, textAlign: 'center'}}>
              {currentChat?.users[1]} wants to chat with you about the {currentChat?.eventName} event.
            </Text>
          )}
          <View style={{flexDirection: 'row', justifyContent: 'space-between'}}>
            <TouchableOpacity
              style={{
                padding: 12, // Збільшено padding для кнопок
                backgroundColor: colors.white,
                borderRadius: 18,
                marginRight: 10,
                flex: 1,
                alignItems: 'center',
                borderColor: colors.textSecondary,
                borderWidth: 1,
              }}
              onPress={handleReject}>
              <Text style={{color: colors.textSecondary, fontWeight: '600'}}>{t('decline')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={{
                padding: 12, // Збільшено padding для кнопок
                backgroundColor: colors.secondary,
                borderRadius: 18,
                marginLeft: 10,
                flex: 1,
                alignItems: 'center',
              }}
              onPress={handleAccept}>
              <Text style={{color: colors.white, fontWeight: '600'}}>{t('accept')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Show OfflineChatInput when conditions are met and user is not blocked */}
      {(isAccepted || isInitiator || currentChat?.type == 'contact-pyxi-select') &&
        !finalBlockStatus &&
        currentChat?.status != 'closed' && (
          <OfflineChatInput
            submit={onSendMessage}
            h={height}
            progress={progress}
            chatId={key || ''}
            userId={currentUserId || ''}
            userName={`${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim()}
            userImage={currentUser?.photo}
            onMessageStatusChange={handleMessageStatusChange}
            onOfflineMessagesSent={handleOfflineMessagesSent}
          />
        )}

      {/* Offline Message Notification */}
      <OfflineMessageNotification
        visible={showOfflineNotification}
        successCount={offlineNotificationData.successCount}
        failureCount={offlineNotificationData.failureCount}
        onHide={() => setShowOfflineNotification(false)}
      />
    </View>
  );
};

export default ChatWithUsers;
